import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import Pagination from '../components/Pagination';

// ── Types ─────────────────────────────────────────────────────────────────────

interface IssueReportItem {
  id: string;
  user_id: string | null;
  user_email: string | null;
  issue_type: string;
  severity: string | null;
  description: string;
  selected_text: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  has_screenshot: boolean;
}

interface IssueReportDetail extends IssueReportItem {
  screenshot_data_url: string | null;
  annotation_svg: string | null;
  context: Record<string, unknown> | null;
  admin_notes: string | null;
}

interface PaginatedReports {
  items: IssueReportItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (dt: string) =>
  new Date(dt).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const TYPE_LABEL: Record<string, string> = {
  bug: 'Bug',
  visual_oddity: 'Visual oddity',
  suggestion: 'Suggestion',
};

const SEV_COLOR: Record<string, string> = {
  low: '#16a34a',
  medium: '#d97706',
  high: '#dc2626',
};

const STATUS_COLOR: Record<string, string> = {
  open: '#3b82f6',
  acknowledged: '#8b5cf6',
  resolved: '#16a34a',
  wont_fix: '#6b7280',
};

/**
 * The annotation SVG is stored with absolute pixel dimensions matching the
 * original screenshot (e.g. width="2560" height="1440"). To display it as a
 * correctly-scaled overlay on the compressed preview image we:
 *  1. Extract the original width/height from the SVG attributes.
 *  2. Replace them with width="100%" height="100%".
 *  3. Add a viewBox so the SVG coordinate space stays correct.
 */
function scaleAnnotationSvg(svg: string): string {
  const wMatch = svg.match(/\bwidth="(\d+)"/);
  const hMatch = svg.match(/\bheight="(\d+)"/);
  if (!wMatch || !hMatch) return svg;
  const [, w] = wMatch;
  const [, h] = hMatch;
  return svg
    .replace(/\bwidth="\d+"/, 'width="100%"')
    .replace(/\bheight="\d+"/, 'height="100%"')
    .replace('<svg ', `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" `);
}

const Badge = ({ label, color }: { label: string; color: string }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
    fontSize: '11px', fontWeight: 600, color,
    background: `${color}18`, border: `1px solid ${color}44`,
  }}>{label}</span>
);

// ── Detail modal ──────────────────────────────────────────────────────────────

function DetailModal({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const [report, setReport] = useState<IssueReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [imgExpanded, setImgExpanded] = useState(false);

  useEffect(() => {
    api<IssueReportDetail>(`/admin/issue-reports/${reportId}`)
      .then(r => {
        setReport(r);
        setNotes(r.admin_notes ?? '');
        setStatus(r.status);
      })
      .finally(() => setLoading(false));
  }, [reportId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api<IssueReportDetail>(`/admin/issue-reports/${reportId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, admin_notes: notes }),
      });
      setReport(updated);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        style={{
          background: 'var(--bg)', borderRadius: '10px', width: '720px', maxHeight: '90vh',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          border: '1px solid var(--border)', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: '15px' }}>
            Issue Report
            {report && <span style={{ color: 'var(--tx3)', fontWeight: 400, marginLeft: 8, fontSize: '12px' }}>#{report.id.slice(0, 8).toUpperCase()}</span>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx3)' }}>Loading…</div>
        ) : report ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {/* Meta row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <Badge label={TYPE_LABEL[report.issue_type] ?? report.issue_type} color="#6366f1" />
              {report.severity && <Badge label={report.severity} color={SEV_COLOR[report.severity] ?? '#6b7280'} />}
              <Badge label={report.status.replace('_', ' ')} color={STATUS_COLOR[report.status] ?? '#6b7280'} />
              <span style={{ fontSize: '12px', color: 'var(--tx3)', marginLeft: 'auto' }}>{fmt(report.created_at)}</span>
            </div>

            {/* Reporter */}
            {report.user_email && (
              <div style={{ fontSize: '12px', color: 'var(--tx2)', marginBottom: 14 }}>
                Reporter: <strong>{report.user_email}</strong>
              </div>
            )}

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <div style={sectionLabel}>Description</div>
              <div style={{ fontSize: '13px', color: 'var(--tx)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg2)', borderRadius: 6, padding: '10px 12px' }}>
                {report.description}
              </div>
            </div>

            {/* Selected text */}
            {report.selected_text && (
              <div style={{ marginBottom: 16 }}>
                <div style={sectionLabel}>Selected text</div>
                <div style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--tx2)', background: 'var(--bg2)', borderRadius: 6, padding: '8px 12px' }}>
                  "{report.selected_text}"
                </div>
              </div>
            )}

            {/* Context */}
            {report.context && (
              <div style={{ marginBottom: 16 }}>
                <div style={sectionLabel}>Page context</div>
                <div style={{ fontSize: '11px', color: 'var(--tx2)', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  <>
                    {report.context.url && <span style={ctxBadge}>{String(report.context.url)}</span>}
                    {report.context.userAgent && <span style={{ ...ctxBadge, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(report.context.userAgent)}</span>}
                  </>
                </div>
              </div>
            )}

            {/* Screenshot */}
            {report.screenshot_data_url && (
              <div style={{ marginBottom: 16 }}>
                <div style={sectionLabel}>Screenshot</div>
                {/* position:relative is required so the absolute annotation overlay
                    is contained within this box, not the modal body */}
                <div
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: '1px solid var(--border)',
                    maxHeight: imgExpanded ? 'none' : 240,
                  }}
                  onClick={() => setImgExpanded(v => !v)}
                >
                  <img
                    src={report.screenshot_data_url}
                    alt="Screenshot"
                    style={{ width: '100%', display: 'block' }}
                  />
                  {/* Annotation SVG overlay — SVG is scaled via viewBox so the
                      red rectangle tracks the exact region of the image */}
                  {report.annotation_svg && (
                    <div
                      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                      dangerouslySetInnerHTML={{ __html: scaleAnnotationSvg(report.annotation_svg) }}
                    />
                  )}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--tx3)', marginTop: 4 }}>
                  {imgExpanded ? 'Click to collapse' : 'Click to expand'}
                </div>
              </div>
            )}

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginBottom: 16 }} />

            {/* Admin controls */}
            <div style={{ marginBottom: 12 }}>
              <div style={sectionLabel}>Status</div>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="select-input"
                style={{ width: '100%' }}
              >
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
                <option value="wont_fix">Won't Fix</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={sectionLabel}>Admin notes</div>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes…"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '8px 10px', fontSize: '13px',
                  color: 'var(--tx)', resize: 'vertical', outline: 'none',
                }}
              />
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              style={{ width: '100%' }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx3)' }}>Report not found.</div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function IssueReportsPage() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [data, setData] = useState<PaginatedReports | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: '30' });
    if (typeFilter) params.set('type', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    try {
      setData(await api<PaginatedReports>(`/admin/issue-reports?${params}`));
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-content">
      <div style={{ marginBottom: 20 }}>
        <div className="overline">Platform</div>
        <h1 className="page-title">Issue Reports</h1>
        {data && <div className="page-subtitle">{data.total} total</div>}
      </div>

      {/* Filters */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <select
          className="select-input"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
          <option value="wont_fix">Won't Fix</option>
        </select>
        <select
          className="select-input"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
        >
          <option value="">All types</option>
          <option value="bug">Bug</option>
          <option value="visual_oddity">Visual oddity</option>
          <option value="suggestion">Suggestion</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: 'var(--tx3)', padding: '40px', textAlign: 'center' }}>Loading…</div>
      ) : !data?.items.length ? (
        <div style={{ color: 'var(--tx3)', padding: '40px', textAlign: 'center' }}>No reports found.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Reporter</th>
              <th>Screenshot</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(r => (
              <tr key={r.id} onClick={() => setSelectedId(r.id)} style={{ cursor: 'pointer' }}>
                <td>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--tx2)' }}>
                    #{r.id.slice(0, 8).toUpperCase()}
                  </span>
                </td>
                <td><Badge label={TYPE_LABEL[r.issue_type] ?? r.issue_type} color="#6366f1" /></td>
                <td>
                  {r.severity
                    ? <Badge label={r.severity} color={SEV_COLOR[r.severity] ?? '#6b7280'} />
                    : <span style={{ color: 'var(--tx3)' }}>—</span>}
                </td>
                <td><Badge label={r.status.replace('_', ' ')} color={STATUS_COLOR[r.status] ?? '#6b7280'} /></td>
                <td style={{ fontSize: '12px', color: 'var(--tx2)' }}>{r.user_email ?? 'Anonymous'}</td>
                <td style={{ fontSize: '12px', color: r.has_screenshot ? '#16a34a' : 'var(--tx3)' }}>
                  {r.has_screenshot ? '✓' : '—'}
                </td>
                <td style={{ fontSize: '12px', color: 'var(--tx3)' }}>{fmt(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && data.total_pages > 1 && (
        <Pagination
          page={page}
          pages={data.total_pages}
          total={data.total}
          perPage={30}
          onPage={setPage}
        />
      )}

      {selectedId && (
        <DetailModal reportId={selectedId} onClose={() => { setSelectedId(null); load(); }} />
      )}
    </div>
  );
}

// ── Style constants ───────────────────────────────────────────────────────────

const sectionLabel: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: 'var(--tx3)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
};

const ctxBadge: React.CSSProperties = {
  display: 'inline-block', background: 'var(--bg3)',
  borderRadius: 4, padding: '2px 6px', fontSize: '10px', color: 'var(--tx2)',
};
